import { ChangeDetectionStrategy, Component, computed } from '@angular/core';

import { ReadonlyDisplayBase } from './readonly-display.base';
import { StatusBadgeComponent } from '../../../status-badge.component';
import { resolveOptionBadge } from '../../../../utils/option-badge.util';
import { modulesStoreOf } from '../display-context.util';
import type { LinkedEntryEnvelope } from '../readonly.types';
import { LinkedEntryDisplayComponent } from './linked-entry-display.component';
import { ValueDisplayComponent } from './value-display.component';

/**
 * Read display for option fields (badge-rule unification) — three outcomes, decided in this
 * order (I3: corrects an earlier "two outcomes" that undercounted its own list by one, and a
 * claim below about the table that was never true).
 *
 * 1. THE SELECTED OPTION HAS A COLOUR: any single-valued option field (manual or taxonomy) with a
 *    coloured answer badges, full stop — `resolveOptionBadge` (`shared/utils/option-badge.util.ts`)
 *    is the ONE place that decision is made. The list column is a SEPARATE call site, not this
 *    component: `table.component.ts`'s `optionBadgeFor` renders `app-status-badge` directly for
 *    its cells, and only the read row reaches this component at all, via
 *    `readonly-display.registry.ts` (`custom-select`/`select` → `StatusDisplayComponent`). Both
 *    that call site and the detail header (`DetailsService.statusBadge`) still agree with the read
 *    row by construction, not by convention — they resolve the same option colour through the
 *    same function, they just do not share this component to render it. There is no more
 *    per-field opt-in and no module-level mapping to keep in sync with the option's own colour
 *    (#20811's `SourceField.statusBadge` / `Module.statusBadgeMapping`) — one switch, the colour
 *    on the option itself.
 * 2. AN ENVELOPE ANSWER with no badge is a reference to an entry in another module — what a
 *    module-backed option set stores (`moduleValueType: 'entry'`) — so it is handed to
 *    `LinkedEntryDisplayComponent` and reads as a link to that item. Clicking through is the
 *    default since the user decision of 2026-09-10 (task 21258b).
 * 3. EVERYTHING ELSE is the option LABEL as plain text, through the one shared text rule
 *    (`ValueDisplayComponent`).
 *
 * Presentational: the module's taxonomy store comes from `field.options.formState.modulesStore`,
 * which `DetailsService` publishes on every entry form — no store is injected, so a story renders
 * this with plain inputs either way, taxonomy colour simply unresolved.
 */
@Component({
  selector: 'dd-status-display',
  imports: [StatusBadgeComponent, LinkedEntryDisplayComponent, ValueDisplayComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // display: contents — the display must not become a box inside the value span, or the row's
  // truncate/multiline behaviour would stop at this element.
  styles: `
    :host {
      display: contents;
    }
  `,
  template: `
    @if (badge(); as badge) {
      <app-status-badge [label]="badge.label" [variant]="badge.variant" [customColors]="badge.customColors" />
    } @else if (isLinkedEntry()) {
      <dd-linked-entry-display [field]="field()" [value]="value()" />
    } @else {
      <dd-value-display [field]="field()" [value]="value()" />
    }
  `,
})
export class StatusDisplayComponent extends ReadonlyDisplayBase {
  /**
   * The badge for this field's value, or `undefined` when the field is not a single-valued option
   * field, or its selected option carries no colour — an uncoloured value is better read as itself
   * than as a guessed tone.
   */
  readonly badge = computed(() => resolveOptionBadge(this.field(), this.value(), modulesStoreOf(this.field())));

  /** Outcome 2: an entry reference on a field whose value did not already earn a badge. */
  readonly isLinkedEntry = computed(() => !this.badge() && holdsEntryEnvelope(this.value()));
}

/**
 * True when the answer carries at least one ENVELOPE — an object with an entry id or a nested
 * entry, the shape a module-backed option set stores (dynamic-options.service.ts:632-647) and the
 * only one a route can be built from. A taxonomy answer (`{ slug, selectedInput }`) and a plain
 * option code have neither, so they stay on the text display.
 *
 * A false positive here would still be harmless — `LinkedEntryDisplayComponent` falls back to the
 * same `dd-value-display` when it cannot place an item — but keeping the common case off that
 * path avoids a component layer per plain option row.
 */
function holdsEntryEnvelope(value: unknown): boolean {
  const items = Array.isArray(value) ? value : [value];
  return items.some(item => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      return false;
    }
    const envelope = item as LinkedEntryEnvelope;
    return typeof envelope.id === 'string' || !!envelope.entry;
  });
}
